import { useMemo } from 'react';
import './global.css';
import { Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { getInitialLanguage } from './i18n.js';
import { AppProviders } from './context/AppProviders.js';
import { usePublicSettings } from './context/PublicSettingsContext.js';
import Header from './components/layout/Header/Header.js';
import Home from './components/Home/Home/Home.js';
import About from './pages/About/About.js';
import Steps from './components/Home/Steps/Steps.js';
import HomeOurStory from './components/Home/HomeOurStory/HomeOurStory.js';
import Gallery from './components/Home/Gallery/Gallery.js';
import PopularDishes from './components/Home/PopularDishes/PopularDishes.js';
import Faqs from './components/Home/Faqs/Faqs.js';
import Testimonials from './components/Testimonials/Testimonials.js';
import Footer from './components/layout/Footer/Footer.js';
import Menu from './pages/Menu/Menu.jsx';
import ScrollUp from "./components/ScrollUp/ScrollUp.jsx";
import ScrollToTop from "./utils/ScrollToTop.js";
import InstallPrompt from "./components/Home/InstallPrompt/InstallPrompt.js";
import Wishlist from './pages/Wishlist/Wishlist.js';
import Cart from './pages/Cart/Cart.js';
import Checkout from './pages/Checkout/Checkout.js';
import Blog from './pages/Blog/Blog.js';
import BlogDetails from './pages/BlogDetails/BlogDetails.js';
import Reservation from './pages/Reservation/Reservation.js';
import ContactPage from './pages/ContactPage/ContactPage.js';
import AuthRoutes from './pages/Auth/AuthRoutes.js';
import OrderSuccess from "./pages/OrderSuccess/OrderSuccess";
import TrackOrder from "./pages/TrackOrder/TrackOrder";
import MyOrders from "./pages/MyOrders/MyOrders";
import NotFound from "./pages/NotFound/NotFound";
import InstallSection from "./components/InstallSection/InstallSection";
import Unsubscribe from "./pages/Unsubscribe/Unsubscribe";
import Subscribe from "./pages/Subscribe/Subscribe";
import Faq from "./pages/Faq/Faq.js";
import Events from "./pages/Events/Events.js";
import GalleryPage from "./pages/Gallery/Gallery.jsx";
import PrivacyPolicy from './pages/PrivacyPolicy/PrivacyPolicy.js';
import TermsOfService from './pages/TermsOfService/TermsOfService.js';
import Theme from './pages/Theme/Theme.jsx';
import ReservationForm from "./components/ReservationForm/ReservationForm";
import SupportChat from "./components/SupportChat/SupportChat.jsx";
import { dualStoreRoute } from './routes/dualStoreRoute.jsx';
import StoreScopedRedirect from './routes/StoreScopedRedirect.jsx';
import PageViewBeacon from './components/storefront/PageViewBeacon.jsx';
import StoreHead from './components/storefront/StoreHead.jsx';


function isPageEnabled(slug, publicSettings) {
    if (!publicSettings?.storefrontPages || typeof publicSettings.storefrontPages !== 'object') {
        return true;
    }
    const entry = publicSettings.storefrontPages[slug];
    if (!entry || typeof entry !== 'object') return true;
    return entry.enabled !== false;
}

const AppContent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { publicSettings } = usePublicSettings();

    // Compute initial language once to avoid re-computation on every render
    const initialLanguage = useMemo(() => getInitialLanguage(), []);

    const handleScrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    const isEnabled = (slug) => isPageEnabled(slug, publicSettings);

    return (
        <>
            <StoreHead />
            <ScrollToTop />
            <ScrollUp />

            <Header />
            <Routes>
                <Route path="/" element={<Navigate to={`/${initialLanguage}`} replace />} />
                <Route path="/s/:orgSlug/:storeSlug" element={<StoreScopedRedirect />} />

                {dualStoreRoute('/:lang', (
                    <main id="main-content">
                        <Home />
                        <PopularDishes navigate={navigate} />
                        <HomeOurStory />
                        <Gallery />
                        <Steps />
                        <Testimonials />
                        {isEnabled('faq') && <Faqs />}
                        <ReservationForm />
                        <InstallSection />
                    </main>
                ), 'home')}

                {isEnabled('about') && dualStoreRoute('/:lang/about', <About />, 'about')}
                {dualStoreRoute('/:lang/menu', <Menu />, 'menu')}
                {isEnabled('wishlist') && dualStoreRoute('/:lang/wishlist', <Wishlist />, 'wishlist')}
                {dualStoreRoute('/:lang/cart', <Cart />, 'cart')}
                {dualStoreRoute('/:lang/checkout', <Checkout />, 'checkout')}
                {dualStoreRoute('/:lang/order-success', <OrderSuccess />, 'order-success')}
                {isEnabled('track-order') && dualStoreRoute('/:lang/track-order', <TrackOrder />, 'track-order')}
                {isEnabled('my-orders') && dualStoreRoute('/:lang/my-orders', <MyOrders />, 'my-orders')}
                {isEnabled('blog') && dualStoreRoute('/:lang/blog', <Blog />, 'blog')}
                {isEnabled('blog') && dualStoreRoute('/:lang/blog/:id', <BlogDetails />, 'blog-details')}
                {isEnabled('reservation') && dualStoreRoute('/:lang/reservation', <Reservation />, 'reservation')}
                {isEnabled('contact') && dualStoreRoute('/:lang/contact', <ContactPage />, 'contact')}
                {isEnabled('subscribe') && dualStoreRoute('/:lang/subscribe', <Subscribe />, 'subscribe')}
                {isEnabled('faq') && dualStoreRoute('/:lang/faq', <Faq />, 'faq')}
                {isEnabled('events') && dualStoreRoute('/:lang/events', <Events />, 'events')}
                {isEnabled('gallery') && dualStoreRoute('/:lang/gallery', <GalleryPage />, 'gallery')}
                {isEnabled('unsubscribe') && dualStoreRoute('/:lang/unsubscribe', <Unsubscribe />, 'unsubscribe')}
                {dualStoreRoute('/:lang/theme', <Theme />, 'theme')}
                {isEnabled('privacy-policy') && dualStoreRoute('/:lang/privacy-policy', <PrivacyPolicy />, 'privacy')}
                {isEnabled('terms-of-service') && dualStoreRoute('/:lang/terms-of-service', <TermsOfService />, 'terms')}

                {
                    AuthRoutes(initialLanguage)
                }

                {/* Fallback for routes without language - redirect to current language */}
                <Route path="/menu" element={<Navigate to={`/${initialLanguage}/menu`} replace />} />
                {isEnabled('wishlist') && <Route path="/wishlist" element={<Navigate to={`/${initialLanguage}/wishlist`} replace />} />}
                <Route path="/cart" element={<Navigate to={`/${initialLanguage}/cart`} replace />} />
                <Route path="/checkout" element={<Navigate to={`/${initialLanguage}/checkout`} replace />} />
                <Route path="/order-success" element={<Navigate to={`/${initialLanguage}/order-success${location.search}`} replace />} />
                {isEnabled('track-order') && <Route path="/track-order" element={<Navigate to={`/${initialLanguage}/track-order${location.search}`} replace />} />}
                {isEnabled('my-orders') && <Route path="/my-orders" element={<Navigate to={`/${initialLanguage}/my-orders`} replace />} />}
                <Route path="/login" element={<Navigate to={`/${initialLanguage}/sign-in`} replace />} />
                <Route path="/register" element={<Navigate to={`/${initialLanguage}/sign-up`} replace />} />
                <Route path="/request-reset" element={<Navigate to={`/${initialLanguage}/forgot-password`} replace />} />
                <Route path="/reset-password" element={<Navigate to={`/${initialLanguage}/reset-password${location.search}`} replace />} />
                <Route path="/profile" element={<Navigate to={`/${initialLanguage}/profile`} replace />} />
                {isEnabled('blog') && <Route path="/blog" element={<Navigate to={`/${initialLanguage}/blog`} replace />} />}
                {isEnabled('reservation') && <Route path="/reservation" element={<Navigate to={`/${initialLanguage}/reservation`} replace />} />}
                {isEnabled('contact') && <Route path="/contact" element={<Navigate to={`/${initialLanguage}/contact`} replace />} />}
                {isEnabled('about') && <Route path="/about" element={<Navigate to={`/${initialLanguage}/about`} replace />} />}
                {isEnabled('subscribe') && <Route path="/subscribe" element={<Navigate to={`/${initialLanguage}/subscribe`} replace />} />}
                {isEnabled('faq') && <Route path="/faq" element={<Navigate to={`/${initialLanguage}/faq`} replace />} />}
                {isEnabled('events') && <Route path="/events" element={<Navigate to={`/${initialLanguage}/events`} replace />} />}
                {isEnabled('gallery') && <Route path="/gallery" element={<Navigate to={`/${initialLanguage}/gallery`} replace />} />}
                {isEnabled('unsubscribe') && <Route path="/unsubscribe" element={<Navigate to={`/${initialLanguage}/unsubscribe${location.search}`} replace />} />}

                {/* 404 Not Found - catch all */}
                <Route path="*" element={<NotFound />} />
            </Routes>
            <Footer handleScrollToTop={handleScrollToTop} />
            <InstallPrompt />
            <SupportChat />
            <PageViewBeacon />
        </>
    );
};

const App = () => {
    return (
        <AppProviders>
            <AppContent />
        </AppProviders>
    );
};

export default App;
